import React, { useState, forwardRef } from "react";
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  useReactTable,
  createColumnHelper,
} from "@tanstack/react-table";
import {
  createStyles,
  Table,
  ScrollArea,
  Center,
  Paper,
  Overlay,
  Loader,
  packSx,
  useComponentDefaultProps,
} from "@mantine/core";

import { IDataTableProps } from "./DataTable.types";
import { Th } from "./Th";
import { Pagination } from "./Pagination";
import { Toolbar } from "./Toolbar";
import { ForwardRefWithStaticComponents } from "@mantine/utils";
import { Column } from "./Column";
import useCellDescriptor from "./utils/useCellDescriptor";

const useStyles = createStyles((theme) => ({
  th: {
    padding: "0 !important",
    //width: _params.header.getSize(),
    position: "relative",
  },

  control: {
    width: "100%",
    padding: `${theme.spacing.md}px ${theme.spacing.md}px`,

    "&:hover": {
      backgroundColor:
        theme.colorScheme === "dark"
          ? theme.colors.dark[6]
          : theme.colors.gray[0],
    },
  },

  icon: {
    width: 21,
    height: 21,
    borderRadius: 21,
  },
  resizer: {
    top: 0,
    right: 0,
    bottom: 0,
    width: 16,
    position: "absolute",
    cursor: "ew-resize",
  },
}));

const defaultProps: Partial<IDataTableProps> = {
  sortable: false,
  resizable: false,
  showToolbar: true,
  showFooter: true,
};

type DataTableComponent = ForwardRefWithStaticComponents<
  IDataTableProps,
  { Column: typeof Column }
>;

const columnHelper = createColumnHelper<any>();

export const DataTable: DataTableComponent = forwardRef<any, IDataTableProps>(
  (props, ref) => {
    const {
      columns,
      data,
      sortable,
      totalCount: controlledTotalCount,
      hasPagination = true,
      loading,
      showToolbar,
      showFooter,
      resizable,
      columnResizeMode,
      sx,
      fetchData,
      children,
      ...rest
    } = useComponentDefaultProps("Table", defaultProps, props);

    const tableRef = (ref as any) || React.createRef<HTMLElement>();
    const [controlledPageSize, setControlledPageSize] = useState<number>(10);
    const { classes, cx } = useStyles();

    const { columns: columnDefs } = useCellDescriptor(children as any);

    const allColumns = columnDefs.map((col) =>
      columnHelper.accessor(col.fieldName, {
        header: col.header,
        size: col.size,
        cell: col.cellTemplate ? col.cellTemplate : (info) => info.getValue(),
        footer: col.footer,
      })
    );

    const table = useReactTable({
      data: data as any,

      columns: columns ?? (allColumns as any),
      pageCount: Math.ceil(controlledTotalCount / controlledPageSize),
      getCoreRowModel: getCoreRowModel(),
      getSortedRowModel: getSortedRowModel(),
      getPaginationRowModel: getPaginationRowModel(),
      columnResizeMode: columnResizeMode,
      manualPagination: hasPagination,
      manualSorting: true,
      manualFiltering: true,
      enableColumnResizing: resizable,
    });

    const { state } = table.options;
    const { pagination, sorting, columnFilters } = state;
    const { pageIndex, pageSize } = pagination ?? {
      pageIndex: 0,
      pageSize: 10,
    };

    React.useEffect(() => {
      tableRef.current.state = state;
      fetchData?.({ pageIndex, pageSize });
    }, [fetchData, pageIndex, pageSize]);

    return (
      <Paper sx={{ position: "relative" }} ref={tableRef}>
        {showToolbar && <Toolbar table={table} />}
        <Paper withBorder radius={0}>
          <ScrollArea>
            <Table
              striped
              highlightOnHover
              withColumnBorders
              //withBorder
              horizontalSpacing="md"
              verticalSpacing="md"
              sx={[
                {
                  //tableLayout: "fixed",
                  //minWidth: 700,
                  width: table.getCenterTotalSize(),
                },
                ...packSx(sx),
              ]}
            >
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header, thIndex) => (
                      // <th
                      //   {...{
                      //     key: header.id,
                      //     colSpan: header.colSpan,
                      //     style: {
                      //       width: header.getSize(),
                      //       position: "relative",
                      //     },
                      //   }}
                      // >
                      //   {header.isPlaceholder
                      //     ? null
                      //     : flexRender(
                      //         header.column.columnDef.header,
                      //         header.getContext()
                      //       )}
                      //   <div
                      //     {...{
                      //       onMouseDown: header.getResizeHandler(),
                      //       onTouchStart: header.getResizeHandler(),
                      //       className: classes.resizer,
                      //       // className: `resizer ${
                      //       //   header.column.getIsResizing() ? 'isResizing' : ''
                      //       // }`,
                      //       style: {
                      //         transform:
                      //           columnResizeMode === "onEnd" &&
                      //           header.column.getIsResizing()
                      //             ? `translateX(${
                      //                 table.getState().columnSizingInfo
                      //                   .deltaOffset
                      //               }px)`
                      //             : "",
                      //       },
                      //     }}
                      //   />
                      // </th>
                      <Th
                        table={table}
                        header={header}
                        key={thIndex}
                        sortable={columnDefs[thIndex].sortable}
                        resizable={resizable}
                        columnResizeMode={columnResizeMode}
                      />
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map((row) => (
                  <tr key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
              {showFooter && (
                <tfoot>
                  {table.getFooterGroups().map((footerGroup) => (
                    <tr key={footerGroup.id}>
                      {footerGroup.headers.map((header) => (
                        <th key={header.id}>
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.footer,
                                header.getContext()
                              )}
                        </th>
                      ))}
                    </tr>
                  ))}
                </tfoot>
              )}
            </Table>
          </ScrollArea>
        </Paper>
        {loading && (
          <Overlay opacity={0.4}>
            <Center sx={{ height: "100%" }}>
              <Loader />
            </Center>
          </Overlay>
        )}
        <Pagination table={table} totalCount={controlledTotalCount} />
      </Paper>
    );
  }
) as any;

DataTable.displayName = "@modir/ui-mantine/Table";
DataTable.Column = Column;
